import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import Card from 'components/card';

const itemKey = (item: any) => String(item._id ?? item.productId);

const DuplicateOrderModal = (props: {
    isOpen: any;
    onClose: any;
    order: any;
    onConfirm: (itemIds?: string[]) => void;
}) => {
    const { isOpen, onClose, order, onConfirm } = props;

    const [mode, setMode] = useState<'all' | 'partial'>('all');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (isOpen) {
            setMode('all');
            setSelectedIds(new Set((order?.items || []).map(itemKey)));
        }
    }, [isOpen, order]);

    const toggleItem = (key: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const items = order?.items || [];

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="relative !z-[1002] !m-auto !w-max min-w-[350px] !max-w-[90%] top-[8vh]">
                <ModalBody>
                    <Card extra="px-[20px] sm:px-[30px] pt-[25px] sm:pt-[35px] pb-[30px] sm:pb-[40px] max-w-[650px] flex flex-col !z-[1004] max-h-[80vh] overflow-y-auto">
                        <h1 className="text-xl font-bold text-navy-700">
                            Dupliquer la commande
                        </h1>
                        <p className="mt-1 text-xs sm:text-sm text-gray-500">
                            La nouvelle commande reprendra le numéro{' '}
                            <span className="font-medium">{order?.orderId}-BIS</span>.
                        </p>

                        <div className="mt-4 flex gap-2">
                            <button
                                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition duration-200 ${
                                    mode === 'all'
                                        ? 'border-toola-500 bg-toola-500 text-white'
                                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setMode('all')}
                            >
                                Toute la commande
                            </button>
                            <button
                                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition duration-200 ${
                                    mode === 'partial'
                                        ? 'border-toola-500 bg-toola-500 text-white'
                                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setMode('partial')}
                            >
                                Sélectionner les produits
                            </button>
                        </div>

                        {mode === 'partial' && (
                            <>
                                <div className="mt-3 flex gap-3">
                                    <p
                                        className="text-xs text-toola-500 hover:cursor-pointer hover:underline"
                                        onClick={() => setSelectedIds(new Set(items.map(itemKey)))}
                                    >
                                        Tout sélectionner
                                    </p>
                                    <p
                                        className="text-xs text-toola-500 hover:cursor-pointer hover:underline"
                                        onClick={() => setSelectedIds(new Set())}
                                    >
                                        Tout désélectionner
                                    </p>
                                </div>
                                <div className="mt-2 max-h-[300px] overflow-y-auto border border-gray-100 rounded-xl">
                                    {items.map((item: any) => {
                                        const key = itemKey(item);
                                        return (
                                            <label
                                                key={key}
                                                className="flex items-center gap-3 px-3 py-2 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(key)}
                                                    onChange={() => toggleItem(key)}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm text-navy-700 truncate">
                                                        {item.name}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        Qté : {item.quantity} {item.unity || ''} · {Number(item.price).toFixed(2)}€
                                                    </p>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        <div className="mt-5 flex gap-2 mx-auto">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition duration-200 hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                disabled={mode === 'partial' && selectedIds.size === 0}
                                className="linear flex items-center justify-center rounded-xl bg-toola-500 px-5 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 active:bg-toola-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() =>
                                    onConfirm(mode === 'all' ? undefined : Array.from(selectedIds))
                                }
                            >
                                Dupliquer
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DuplicateOrderModal;
